import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { PageHeader } from "@/components/PageHeader";
import { inr, formatDateTime } from "@/lib/format";
import { CalendarClock } from "lucide-react";

export default function Followups() {
  const [days, setDays] = useState("7");
  const { data: items = [], isLoading } = useQuery({
    queryKey: ["followups", days],
    queryFn: async () => (await api.get("/leads/followups", { params: { days: Number(days) } })).data,
  });

  return (
    <div data-testid="followups-page">
      <PageHeader title="Follow-up Tracker" subtitle={`${items.length} upcoming follow-ups`}>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-40 bg-white dark:bg-slate-950" data-testid="followups-days-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {[["1", "Today"], ["3", "Next 3 days"], ["7", "Next 7 days"], ["14", "Next 14 days"], ["30", "Next 30 days"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageHeader>

      <Card className="border-slate-200 dark:border-slate-800 overflow-hidden p-0">
        <Table data-testid="followups-table">
          <TableHeader>
            <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50">
              <TableHead className="uppercase text-xs tracking-wider">Follow-up At</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Name</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Phone</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Loan Type</TableHead>
              <TableHead className="uppercase text-xs tracking-wider text-right">Amount</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Assigned To</TableHead>
              <TableHead className="uppercase text-xs tracking-wider">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 6 }).map((_, i) => <TableRow key={i}><TableCell colSpan={7}><Skeleton className="h-8 w-full" /></TableCell></TableRow>)}
            {!isLoading && items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-10"><CalendarClock className="h-8 w-8 mx-auto mb-2 opacity-40" />No follow-ups in this window.</TableCell></TableRow>}
            {!isLoading && items.map((f) => (
              <TableRow key={f.id} data-testid={`followup-row-${f.id}`}>
                <TableCell className="font-num font-semibold text-amber-700 dark:text-amber-400">{formatDateTime(f.follow_up_at)}</TableCell>
                <TableCell className="font-medium text-slate-900 dark:text-slate-100">{f.name || "—"}</TableCell>
                <TableCell className="font-num text-slate-600 dark:text-slate-300">{f.phone || "—"}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{f.loan_type || "—"}</TableCell>
                <TableCell className="text-right font-num font-semibold">{inr(f.loan_amount)}</TableCell>
                <TableCell className="text-slate-600 dark:text-slate-300">{f.assigned_to_name || "—"}</TableCell>
                <TableCell><StatusBadge status={f.status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
